import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { MODULE_LIST, STATUS_COLORS } from "@/config/modules";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DailyReportButton } from "@/components/AdminReports";


function daysLabel(d) {
  if (d < 0) return { text: `${Math.abs(d)}d overdue`, cls: "text-red-600 dark:text-red-400" };
  if (d === 0) return { text: "Today", cls: "text-amber-600 dark:text-amber-400" };
  return { text: `${d}d left`, cls: d <= 7 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground" };
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  const counts = data?.counts || {};
  const gp = data?.gate_pass;
  const sec = data?.security;
  const saf = data?.safety;
  const { can, isAdmin } = useAuth();
  const mods = MODULE_LIST.filter((m) => can(m.key, "view"));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline text-muted-foreground mb-1">Dashboard</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Good to see you, {user?.name?.split(" ")[0]}</h1>
          <p className="text-muted-foreground text-sm mt-1">Your single source of truth for all project information.</p>
        </div>
        {isAdmin && <DailyReportButton />}
      </div>

      {/* Module cards */}
      <div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {mods.map((m, i) => {
            const Icon = Icons[m.icon] || Icons.Circle;
            return (
              <motion.button key={m.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                onClick={() => navigate(m.customPath || `/m/${m.key}`)} data-testid={`card-${m.key}`}
                className="text-left p-4 rounded-xl bg-card border border-border hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200 group">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${m.accent}1a`, color: m.accent }}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="font-display font-semibold text-sm">{m.name}</div>
                <div className="text-2xl font-display font-bold mt-0.5">{counts[m.key] ?? 0}</div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Gate Pass summary (RBAC-gated) — below module menu */}
      {gp && (
        <div className="rounded-xl border border-border overflow-hidden" data-testid="gate-pass-summary" style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #2d5486 100%)" }}>
          <div className="p-5 flex flex-wrap items-center gap-6 text-white">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-white/15 flex items-center justify-center"><Icons.ClipboardCheck className="h-5 w-5" /></div>
              <div>
                <div className="font-display font-bold text-lg leading-tight">Gate Pass</div>
                <div className="text-[12px] text-white/70">Material &amp; vehicle movements</div>
              </div>
            </div>
            <div className="flex gap-8 flex-1">
              <button onClick={() => navigate("/gate-pass?status=issued")} className="text-left" data-testid="gp-today-issued">
                <div className="text-3xl font-display font-bold tabular-nums">{gp.today_issued}</div>
                <div className="text-[11px] text-white/70 uppercase tracking-wide">Issued Today</div>
              </button>
              <button onClick={() => navigate("/gate-pass")} className="text-left" data-testid="gp-pending">
                <div className="text-3xl font-display font-bold tabular-nums text-amber-300">{gp.pending}</div>
                <div className="text-[11px] text-white/70 uppercase tracking-wide">Pending</div>
              </button>
              <button onClick={() => navigate("/gate-pass")} className="text-left" data-testid="gp-total">
                <div className="text-3xl font-display font-bold tabular-nums">{gp.total}</div>
                <div className="text-[11px] text-white/70 uppercase tracking-wide">Total</div>
              </button>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" className="gap-1.5 bg-white/15 text-white hover:bg-white/25 border-0" onClick={() => navigate("/gate-pass/verify")} data-testid="gp-verify-shortcut"><Icons.QrCode className="h-4 w-4" />Verify</Button>
              {can("gate_pass", "create") && <Button variant="secondary" className="gap-1.5 bg-white text-slate-900 hover:bg-white/90" onClick={() => navigate("/gate-pass/new")} data-testid="gp-new-shortcut"><Icons.Plus className="h-4 w-4" />New</Button>}
            </div>
          </div>
        </div>
      )}

      {/* Security Entry & Exit summary (RBAC-gated) */}
      {sec && (
        <div className="rounded-xl border border-border overflow-hidden" data-testid="security-summary" style={{ background: "linear-gradient(135deg, #0f766e 0%, #0ea5e9 100%)" }}>
          <div className="p-5 flex flex-wrap items-center gap-6 text-white">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-white/15 flex items-center justify-center"><Icons.ShieldCheck className="h-5 w-5" /></div>
              <div>
                <div className="font-display font-bold text-lg leading-tight">Security Entry &amp; Exit</div>
                <div className="text-[12px] text-white/70">Live gate register · IST</div>
              </div>
            </div>
            <div className="flex gap-8 flex-1">
              <button onClick={() => navigate("/crm/projects/gpi/security")} className="text-left" data-testid="sec-today-entries">
                <div className="text-3xl font-display font-bold tabular-nums">{sec.entries_today}</div>
                <div className="text-[11px] text-white/70 uppercase tracking-wide">Entries Today</div>
              </button>
              <button onClick={() => navigate("/crm/projects/gpi/security?status=inside")} className="text-left" data-testid="sec-inside">
                <div className="text-3xl font-display font-bold tabular-nums">{sec.inside}</div>
                <div className="text-[11px] text-white/70 uppercase tracking-wide">Inside Now</div>
              </button>
              <button onClick={() => navigate("/crm/projects/gpi/security?status=inside")} className="text-left" data-testid="sec-overstaying">
                <div className={`text-3xl font-display font-bold tabular-nums ${sec.overstaying > 0 ? "text-amber-200" : ""}`}>{sec.overstaying}</div>
                <div className="text-[11px] text-white/70 uppercase tracking-wide">Overstaying</div>
              </button>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" className="gap-1.5 bg-white/15 text-white hover:bg-white/25 border-0" onClick={() => window.open("/gpi/security", "_blank")} data-testid="sec-portal-shortcut"><Icons.ExternalLink className="h-4 w-4" />Gate Portal</Button>
              <Button variant="secondary" className="gap-1.5 bg-white text-slate-900 hover:bg-white/90" onClick={() => navigate("/crm/projects/gpi/security")} data-testid="sec-open-shortcut"><Icons.ArrowRight className="h-4 w-4" />Open</Button>
            </div>
          </div>
        </div>
      )}

      {/* Morning Safety Check reminder (RBAC-gated) */}
      {saf && (
        <div className="rounded-xl border overflow-hidden" data-testid="safety-summary" style={{ background: saf.submitted_today ? "linear-gradient(135deg,#065f46 0%,#10b981 100%)" : "linear-gradient(135deg,#b45309 0%,#f59e0b 100%)" }}>
          <div className="p-5 flex flex-wrap items-center gap-6 text-white">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-white/15 flex items-center justify-center"><Icons.ShieldAlert className="h-5 w-5" /></div>
              <div>
                <div className="font-display font-bold text-lg leading-tight">Morning Safety Check</div>
                <div className="text-[12px] text-white/80">{saf.submitted_today ? `Done for today · ${saf.today_photos} photos uploaded` : "Not uploaded yet today — please upload site safety photos"}</div>
              </div>
            </div>
            <div className="flex-1" />
            <div className="flex gap-2">
              {saf.can_submit && !saf.submitted_today && (
                <Button variant="secondary" className="gap-1.5 bg-white text-slate-900 hover:bg-white/90" onClick={() => navigate("/crm/projects/gpi/safety")} data-testid="safety-upload-shortcut"><Icons.Camera className="h-4 w-4" />Upload Now</Button>
              )}
              <Button variant="secondary" className="gap-1.5 bg-white/15 text-white hover:bg-white/25 border-0" onClick={() => navigate("/crm/projects/gpi/safety")} data-testid="safety-open-shortcut"><Icons.ArrowRight className="h-4 w-4" />{saf.submitted_today ? "View" : "Open"}</Button>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Follow-ups */}
        <Card className="p-5 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Pending Follow-ups</h3>
            <Icons.ListTodo className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="space-y-2.5">
            {(data?.pending_followups || []).length === 0 && <p className="text-sm text-muted-foreground">Nothing pending. 🎉</p>}
            {(data?.pending_followups || []).map((f) => (
              <div key={f.id} onClick={() => navigate(`/m/followups`)} className="flex items-start justify-between gap-2 p-2.5 rounded-lg hover:bg-secondary cursor-pointer transition-colors">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{f.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{f.assigned_to || "Unassigned"}</div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {f.priority && <Badge className={`text-[10px] ${STATUS_COLORS[f.priority] || ""}`} variant="secondary">{f.priority}</Badge>}
                  {f.due_date && <span className="text-[11px] text-muted-foreground">{f.due_date}</span>}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Expiry */}
        <Card className="p-5 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Upcoming Expiry</h3>
            <Icons.CalendarClock className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="space-y-2.5">
            {(data?.upcoming_expiry || []).length === 0 && <p className="text-sm text-muted-foreground">No upcoming expiries.</p>}
            {(data?.upcoming_expiry || []).map((e) => {
              const dl = daysLabel(e.days_left);
              return (
                <div key={e.id} onClick={() => navigate(`/m/${e.resource || "documents"}`)} data-testid={`expiry-item-${e.id}`} className="flex items-center justify-between gap-2 p-2.5 rounded-lg hover:bg-secondary cursor-pointer transition-colors">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{e.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{e.type} · {e.category}</div>
                  </div>
                  <span className={`text-xs font-medium shrink-0 ${dl.cls}`}>{dl.text}</span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Recent activity — admin only (audit trail) */}
        {isAdmin && (
        <Card className="p-5 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Recent Activity</h3>
            <Icons.History className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="space-y-2.5">
            {(data?.recent_activity || []).length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
            {(data?.recent_activity || []).map((a) => (
              <div key={a.id} className="flex items-start gap-2.5 text-sm">
                <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                <div className="min-w-0">
                  <span className="font-medium capitalize">{a.action}</span>{" "}
                  <span className="text-muted-foreground">{a.resource}</span>{" "}
                  <span className="truncate">{a.record_name}</span>
                  <div className="text-[11px] text-muted-foreground">{a.user_name} · {new Date(a.timestamp).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
        )}
      </div>
    </div>
  );
}
